import React, { useEffect, useMemo, useRef, useState } from "react";

/* ---------- Hook: detect when section is active ---------- */
function useInView({ threshold = 0.25, rootMargin = "0px 0px -15% 0px" } = {}) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold, rootMargin }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, active };
}

/* ---------- Utils ---------- */
function uniqByLogo(arr) {
  const seen = new Set();
  return arr.filter((x) => {
    const logo = (x?.logo || "").trim();
    if (!logo) return false;
    if (seen.has(logo)) return false;
    seen.add(logo);
    return true;
  });
}

// Drop junk, social icons, fogsv logo, numbered gallery images (01.jpg..999.webp), etc.
function cleanSponsors(arr) {
  const badName = /^(fog social profile|fogsv logos)$/i;
  const numberedFile = /\/(\d{2,3})\.(jpg|jpeg|png|webp)$/i;
  const social = /(facebook|twitter|youtube)_icon/i;

  return uniqByLogo(
    (arr || [])
      .map((s) => ({
        name: (s?.name || "Sponsor").toString().trim() || "Sponsor",
        logo: (s?.logo || "").toString().trim(),
      }))
      .filter((s) => {
        if (!s.logo) return false;
        if (!s.logo.includes("/wp-content/uploads/")) return false;
        if (s.logo.includes("/undefined.")) return false;
        if (s.logo.includes("fogsv_logo")) return false;
        if (social.test(s.logo)) return false;
        if (numberedFile.test(s.logo)) return false;
        if (badName.test(s.name)) return false;
        return true;
      })
  );
}

/* ---------- Sponsor list: click-to-load + paging ---------- */
function SponsorGridOnDemand({ titleId, items, pageSize = 10 }) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);

  // reset if items changes
  useEffect(() => {
    setIsOpen(false);
    setVisibleCount(0);
  }, [items]);

  const canShowMore = visibleCount < items.length;

  const handleClick = () => {
    if (!isOpen) {
      setIsOpen(true);
      setVisibleCount(Math.min(pageSize, items.length));
      return;
    }
    if (canShowMore) {
      setVisibleCount((c) => Math.min(c + pageSize, items.length));
    }
  };

  const btnLabel = !isOpen ? "Show Sponsors" : canShowMore ? "Show More" : "All Sponsors Loaded";

  return (
    <div className="spon-block" aria-labelledby={titleId}>
      <div className="spon-moreWrap">
        <button
          type="button"
          className="spon-moreBtn"
          onClick={handleClick}
          disabled={isOpen && !canShowMore}
        >
          {btnLabel}
        </button>
        {isOpen && (
          <div className="spon-moreHint">
            Showing {Math.min(visibleCount, items.length)} of {items.length}
          </div>
        )}
      </div>

      {/* Nothing renders until clicked */}
      {isOpen && (
        <div className="spon-grid">
          {items.slice(0, visibleCount).map((s) => (
            <div className="spon-card" key={s.logo}>
              <img
                src={s.logo}
                alt={s.name}
                className="spon-logo"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
              <div className="spon-name">{s.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sponsorship2026() {
  const { ref, active } = useInView();

  const sponsors2026_raw = [
    { name: "bmwfrmtpa", logo: "https://fogsv.com/wp-content/uploads/2022/09/bmwfrmtpa.jpg" },
    { name: "FogSV Logos", logo: "https://fogsv.com/wp-content/uploads/2022/09/fogsv_logo.png" },
    { name: "MRT", logo: "https://fogsv.com/wp-content/uploads/2025/02/mrt.webp" },
    { name: "Eastern", logo: "https://fogsv.com/wp-content/uploads/2025/12/eastern.png" },
    { name: "Vadilal", logo: "https://fogsv.com/wp-content/uploads/2026/01/vadilal.webp" },
    { name: "Sponsor", logo: "https://fogsv.com/wp-content/uploads/2025/12/Untitled-design-10.png" },
    { name: "Integrated", logo: "https://fogsv.com/wp-content/uploads/2024/12/integrated.webp" },
    { name: "Sponsor", logo: "https://fogsv.com/wp-content/uploads/2025/12/Untitled-design-4.png" },
    { name: "Sponsor", logo: "https://fogsv.com/wp-content/uploads/2025/12/Untitled-design-5.png" },
    { name: "Daawat", logo: "https://fogsv.com/wp-content/uploads/2025/12/dawaat.png" },
  ];

  const sponsors2025_raw = [
    { name: "Sponsor 6", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor6.webp" },
    { name: "Sponsor 7", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor7.webp" },
    { name: "Signia by Hilton", logo: "https://fogsv.com/wp-content/uploads/2025/07/Signia-by-hilton.webp" },
    { name: "Logo SPN 2", logo: "https://fogsv.com/wp-content/uploads/2025/05/logospn2.webp" },
    { name: "Sponsor 1", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor1.webp" },
    { name: "Sponsor 9", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor9.webp" },
    { name: "Sponsor 5", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor5.webp" },
    { name: "Sponsor 3", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor3.webp" },
    { name: "Sponsor 4", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor4.webp" },
    { name: "Sponsor 10", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor10.webp" },
    { name: "Keep Learners", logo: "https://fogsv.com/wp-content/uploads/2025/07/keep-learners.webp" },
    { name: "Young Champion", logo: "https://fogsv.com/wp-content/uploads/2025/07/YoungChampion.webp" },
    { name: "Ace", logo: "https://fogsv.com/wp-content/uploads/2025/07/ace-1.webp" },
    { name: "Earth Cleanse", logo: "https://fogsv.com/wp-content/uploads/2025/07/Earth-cleanse.webp" },
    { name: "Adobe Express", logo: "https://fogsv.com/wp-content/uploads/2025/07/Adobe-Express-file.jpg" },
    { name: "Sponsor 11", logo: "https://fogsv.com/wp-content/uploads/2025/04/Sponsor11.webp" },
    { name: "Spare Air", logo: "https://fogsv.com/wp-content/uploads/2025/02/spare_air.webp" },
    { name: "Logo SPN 3", logo: "https://fogsv.com/wp-content/uploads/2025/05/logospn3.webp" },
    { name: "Logo SPN 4", logo: "https://fogsv.com/wp-content/uploads/2025/05/logospn4.webp" },
    { name: "MRT", logo: "https://fogsv.com/wp-content/uploads/2025/02/mrt.webp" },
    { name: "Vadilal", logo: "https://fogsv.com/wp-content/uploads/2025/02/vadilal.webp" },
  ];

  // Cleaned lists (fast, small)
  const sponsors2026 = useMemo(() => cleanSponsors(sponsors2026_raw), []);
  const sponsors2025 = useMemo(() => cleanSponsors(sponsors2025_raw), []);

  return (
    <>
      <style>{`
        html, body { overflow-x: hidden; }

        .spon-content{
          opacity: 0;
          transform: translateY(16px);
          filter: blur(6px);
          transition: opacity 650ms ease, transform 650ms ease, filter 650ms ease;
        }
        .spon-content.is-active{
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }

        .spon-sectionTitle{
          margin-top: 32px;
          margin-bottom: 14px;
          font-size: 22px;
        }

        .spon-grid{
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
          margin-top: 14px;
        }
        @media (max-width: 992px){
          .spon-grid{ grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 520px){
          .spon-grid{ grid-template-columns: 1fr; }
        }

        .spon-card{
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 14px;
          padding: 18px;
          text-align: center;
          contain: content; /* reduces layout/paint cost */
        }

        .spon-logo{
          width: 100%;
          height: 90px;
          object-fit: contain;
          margin-bottom: 10px;
        }

        .spon-name{
          font-weight: 700;
          font-size: 14px;
          opacity: 0.95;
          word-break: break-word;
        }

        .spon-moreWrap{
          margin-top: 8px;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 12px;
          flex-wrap: wrap;
        }

        .spon-moreBtn{
          padding: 10px 16px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.10);
          color: #fff;
          font-weight: 700;
          cursor: pointer;
        }
        .spon-moreBtn:hover{ background: rgba(255,255,255,.16); }
        .spon-moreBtn:disabled{ opacity: 0.55; cursor: not-allowed; }

        .spon-moreHint{
          color: rgba(255,255,255,0.85);
          font-size: 13px;
        }

        @media (prefers-reduced-motion: reduce){
          .spon-content{
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <section id="sponsorship-2026">
        <div className="block color-scheme-4">
          {/* Header */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="section-sub-title center">
                  <article
                    ref={ref}
                    className={`section-title-body white spon-content ${active ? "is-active" : ""}`}
                  >
                    <h1 className="head-title hra-topTitle">
                      <span>FOG</span> <span>Sponsorship</span>
                    </h1>
                    <p className="hra-topIntro">
                      Thank you to our sponsors for supporting FOG.
                    </p>
                  </article>
                </div>
              </div>
            </div>
          </div>

          {/* 2026 Sponsors */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <h2 id="spon-2026" className="white spon-sectionTitle">2026 Sponsors</h2>
                {/* Loads 0 images until clicked, then 10 at a time */}
                <SponsorGridOnDemand titleId="spon-2026" items={sponsors2026} pageSize={10} />
              </div>
            </div>
          </div>

          {/* 2025 Sponsors */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <h2 id="spon-2025" className="white spon-sectionTitle">2025 Sponsors</h2>
                {/* Loads 0 images until clicked, then 10 at a time */}
                <SponsorGridOnDemand titleId="spon-2025" items={sponsors2025} pageSize={10} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Sponsorship2026;
